import type { Coupon } from '@monorepo/shared'
import type { CouponQuery } from '@monorepo/shared/schemas/coupons'
import { COUPON_STATUS, COUPON_TYPE } from '@monorepo/shared/schemas/coupons'
import { Download, Plus, RefreshCw, Search, Ticket } from 'lucide-react'
import { useMemo, useState } from 'react'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useCouponsList, useDeleteCoupon } from '../hooks/useCoupons'
import { CouponDrawer } from './CouponDrawer'
import { CouponsTable } from './CouponsTable'

export function CouponsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [deleteCoupon, setDeleteCoupon] = useState<Coupon | null>(null)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | undefined>(
    undefined
  )
  const [showCouponFormDrawer, setShowCouponFormDrawer] = useState(false)

  // Create filters object for API
  const filters = useMemo((): CouponQuery => {
    const filterObj: CouponQuery = {}

    if (searchQuery.trim()) {
      filterObj.search = searchQuery.trim()
    }

    if (statusFilter !== 'all') {
      filterObj.status = statusFilter as CouponQuery['status']
    }

    if (typeFilter !== 'all') {
      filterObj.type = typeFilter as CouponQuery['type']
    }

    return filterObj
  }, [searchQuery, statusFilter, typeFilter])

  // Fetch coupons data from API using convenience hook
  const { coupons, isLoading, error, refetch } = useCouponsList(filters)
  const deleteCouponMutation = useDeleteCoupon()

  // Calculate stats from real data
  const activeCoupons = coupons.filter(
    c => c.status === COUPON_STATUS.active
  ).length
  const draftCoupons = coupons.filter(
    c => c.status === COUPON_STATUS.draft
  ).length
  const archivedCoupons = coupons.filter(
    c => c.status === COUPON_STATUS.archived
  ).length
  const totalRedemptions = coupons.reduce(
    (sum, coupon) => sum + (coupon.times_redeemed || 0),
    0
  )

  const handleCreateCoupon = () => {
    setEditingCoupon(undefined)
    setShowCouponFormDrawer(true)
  }

  const handleEditCoupon = (coupon: Coupon) => {
    setEditingCoupon(coupon)
    setShowCouponFormDrawer(true)
  }

  const handleConfirmDelete = async () => {
    if (deleteCoupon) {
      try {
        await deleteCouponMutation.mutateAsync(deleteCoupon.id)
        setDeleteCoupon(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
        <div>
          <PageTitle
            title='Coupons'
            subtitle='Manage promotional codes and discount coupons'
          />
          {/* Stats Chips */}
          <div className='flex flex-wrap gap-2 mt-3'>
            <Badge variant='secondary' className='text-sm'>
              {coupons.length} Total
            </Badge>
            <Badge variant='default' className='text-sm'>
              {activeCoupons} Active
            </Badge>
            <Badge variant='secondary' className='text-sm'>
              {draftCoupons} Draft
            </Badge>
            <Badge variant='outline' className='text-sm'>
              {archivedCoupons} Archived
            </Badge>
            <Badge variant='outline' className='text-sm'>
              {totalRedemptions} Redemptions
            </Badge>
          </div>
        </div>
        <div className='flex gap-3 items-center w-full md:w-auto'>
          <Button
            onClick={handleCreateCoupon}
            size='lg'
            className='flex-1 md:flex-initial'
          >
            <Plus className='w-5 h-5 mr-2' />
            Create Coupon
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
        <div className='flex gap-2 items-center flex-1 max-w-2xl'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4' />
            <Input
              placeholder='Search coupons by name or code...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pl-10'
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className='w-[150px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Status</SelectItem>
              <SelectItem value={COUPON_STATUS.active}>Active</SelectItem>
              <SelectItem value={COUPON_STATUS.draft}>Draft</SelectItem>
              <SelectItem value={COUPON_STATUS.archived}>Archived</SelectItem>
            </SelectContent>
          </Select>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className='w-[150px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Types</SelectItem>
              <SelectItem value={COUPON_TYPE.percentage}>Percentage</SelectItem>
              <SelectItem value={COUPON_TYPE.fixed_amount}>
                Fixed Amount
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className='flex gap-2 items-center'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>

          <Button variant='outline' size='sm'>
            <Download className='w-4 h-4' />
          </Button>
        </div>
      </div>

      {/* Coupons Content */}
      {isLoading ? (
        <div className='bg-transparent border rounded-lg'>
          <div className='animate-pulse space-y-4 p-6'>
            <div className='grid grid-cols-7 gap-4 pb-4 border-b'>
              {Array.from({ length: 7 }).map((_, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                  key={i}
                  className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                ></div>
              ))}
            </div>
            {Array.from({ length: 8 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
              <div key={i} className='grid grid-cols-7 gap-4 py-3'>
                {Array.from({ length: 7 }).map((_, j) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    key={j}
                    className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                  ></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <Card>
          <CardContent className='pt-16 pb-16'>
            <div className='text-center'>
              <Ticket className='w-16 h-16 mx-auto text-muted-foreground/50 mb-4' />
              <h3 className='text-lg font-medium mb-2'>
                Error loading coupons
              </h3>
              <p className='text-muted-foreground mb-6'>
                There was an error loading your coupons. Please try again.
              </p>
              <Button onClick={() => refetch()}>
                <RefreshCw className='w-4 h-4 mr-2' />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : coupons.length === 0 ? (
        <div className='flex items-center justify-center min-h-[400px]'>
          <div className='text-center'>
            <p className='text-sm text-muted-foreground mb-2'>
              No coupons found
            </p>
            <p className='text-xs text-muted-foreground'>
              Try adjusting your filters
            </p>
          </div>
        </div>
      ) : (
        <CouponsTable
          coupons={coupons}
          onEditCoupon={handleEditCoupon}
          onViewCoupon={handleEditCoupon}
        />
      )}

      <ConfirmationDialog
        open={!!deleteCoupon}
        onOpenChange={open => !open && setDeleteCoupon(null)}
        title='Delete Coupon'
        description={`Are you sure you want to delete "${deleteCoupon?.name}" (${deleteCoupon?.code})? This action cannot be undoned.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        isLoading={deleteCouponMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteCoupon(null)}
      />

      {/* Coupon Form Drawer */}
      <CouponDrawer
        coupon={editingCoupon}
        open={showCouponFormDrawer}
        onOpenChange={setShowCouponFormDrawer}
      />
    </div>
  )
}
